import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useMedia } from 'react-media-match';
import { Rating } from '@/src/interfaces/Rating';
import {
  RecommendCountWrapper,
  RatingStars,
  StarsWrapper,
} from '@/src/components/CustomerRatings/RatingsSummary';
import PercentageCircle from '@/src/components/CustomerRatings/RatingsSummary/RecommendCircle';
import Tags from '@/src/components/CustomerRatings/RatingsSummary/RatingTags';
import useConfig from '@empiriecom/module-components/hooks/useConfig';

const OverviewSummary = styled.div<{ mobile: boolean }>`
  width: ${({ mobile }) => (mobile ? '100%' : '50%')};
  ${({ mobile }) => !mobile && 'margin-right: 3.25rem;'}
  ${({ mobile }) => mobile && 'order: 2;'}
  ${({ mobile }) => mobile && 'padding: 0.75rem 0.75rem 0;'}
`;

const StyledStarsWrapper = styled(StarsWrapper)`
  display: inline-flex;
  padding: 0;
  width: 7.5rem;
`;

const StyledRecommendCountWrapper = styled(RecommendCountWrapper)<{ mobile: boolean }>`
  padding-left: 0;
  margin: ${({ mobile }) => (mobile ? '0.5rem 0;' : '1rem 0;')};
`;

const TagsHeadline = styled.div`
  font-size: 1rem;
  font-weight: bold;
`;

interface RatingsListSummaryProps {
  response: Rating;
}

export const RatingsListSummary: FunctionComponent<RatingsListSummaryProps> = ({
  response,
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const {
    customerRatings: { iconType },
  } = useConfig();
  return (
    <OverviewSummary mobile={isMobile}>
      {isMobile && (
        <StyledStarsWrapper>
          <RatingStars
            iconType={iconType}
            maskWidth={`${parseFloat(response.product.avgRatingFiltered.toFixed(1)) * 20}%`}
          />
          <span>({response.product.reviewCountFiltered})</span>
        </StyledStarsWrapper>
      )}
      <StyledRecommendCountWrapper mobile={isMobile}>
        <PercentageCircle
          summaryValues={{
            recommended: response.product.recommendedCountFiltered,
            reviews: response.product.reviewCountFiltered,
          }}
        />
        <div>
          <FormattedMessage
            id="customer-ratings.overview.recommend-count"
            defaultMessage="<label>{recocount} von {totalcount} Kunden</label> würden dieses Produkt einem Freund empfehlen."
            values={{
              label: (txt) => <strong>{txt}</strong>,
              recocount: response.product.recommendedCountFiltered,
              totalcount: response.product.reviewCountFiltered,
            }}
          />
        </div>
      </StyledRecommendCountWrapper>
      {response.product.tags && response.product.tags.pro && response.product.tags.proAvailable && (
        <>
          <TagsHeadline>
            <FormattedMessage
              id="customer-ratings.overview.headline.pro"
              defaultMessage="Vorteile"
            />
          </TagsHeadline>
          <Tags tags={response.product.tags.pro} />
        </>
      )}
      {response.product.tags &&
        response.product.tags.contra &&
        response.product.tags.contraAvailable && (
          <>
            <TagsHeadline>
              <FormattedMessage
                id="customer-ratings.overview.headline.contra"
                defaultMessage="Nachteile"
              />
            </TagsHeadline>
            <Tags tags={response.product.tags.contra} />
          </>
        )}
    </OverviewSummary>
  );
};

export default RatingsListSummary;
